import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
    FraudReportApiService,
    FraudReportFileFormat,
    FraudReportFilesResponse,
    FraudReportResponse,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {downloadFromBlob, LocalizationService, rxSpinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest, Subscription} from 'rxjs';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../../components/full-table/full-table.component';
import {ButtonColumn, Column, ColumnType, Row, StatusColumn} from '../../../../../components/full-table/table/table.component';
import {mapFraudReportStatusToStyle} from '../../../../../components/status/status.component';
import {DictionaryHolderService, findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {FraudReportDetailsFilterHolderService} from '../../../../../domain/fraud-report-details-filter-holder.service';

const FRAUD_REPORT_FILE_TAB = 'tabFraudReportFiles';

@Component({
    selector: 'af-fraud-report-file-tab',
    templateUrl: './fraud-report-file-tab.component.html',
    styleUrl: './fraud-report-file-tab.component.scss',
    standalone: false,
})
export class FraudReportFileTabComponent implements OnInit, OnDestroy {
    @Input() fraudReport: FraudReportResponse;
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    response: FraudReportFilesResponse;
    exportReportSubscription: Subscription;

    constructor(
        private fraudReportApiService: FraudReportApiService,
        public filterHolderService: FraudReportDetailsFilterHolderService,
        private dictionaryHolderService: DictionaryHolderService,
        private snackbarService: SnackbarService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        this.loadFiles(
            this.filterHolderService.getState(this.fraudReport.id, FRAUD_REPORT_FILE_TAB)?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    ngOnDestroy(): void {
        this.exportReportSubscription?.unsubscribe();
    }

    loadFiles(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.fraudReport.id, FRAUD_REPORT_FILE_TAB)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.fraudReport.id, FRAUD_REPORT_FILE_TAB, this.tableEvent);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUD_REPORT_STATUS),
            this.fraudReportApiService.getFraudReportFiles(this.fraudReport.id)
        ]).pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([fraudReportStatuses, response]) => {
                this.buildDataSource(fraudReportStatuses, response);
            });
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'fraudReportFiles',
            filter: {},
            sortBy: {
                columnName: 'createdDate',
                direction: 'desc',
            },
        };
    }

    private downloadReport(reportId: string, reportType: FraudReportFileFormat): void {
        const request = reportType === FraudReportFileFormat.EXCEL
            ? this.fraudReportApiService.downloadFraudReportExcel(reportId)
            : this.fraudReportApiService.downloadFraudReportPdf(reportId);

        const fileName = this.localizationService.getLocalizedKey('i18n_af_fraudReports#' + this.fraudReport.reportType)
            + ' (' + this.response.organizationName + ')';

        this.exportReportSubscription = request.subscribe({
            next: file => {
                downloadFromBlob(file, reportType === FraudReportFileFormat.EXCEL ? `${fileName}.xlsx` : `${fileName}.pdf`);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageFraudReportSuccessfullyExported');
            }, error: err => {
                this.snackbarService.showSnackBar(
                    'i18n_af_snackBarMessageFailedToDownloadFraudReport#' + reportType,
                    'error',
                    false,
                );
                console.error(err);
            },
        });
    }

    private buildDataSource(
        fraudReportStatuses: ReferenceBookResponse[],
        response: FraudReportFilesResponse,
    ): void {
        this.response = response;
        this.dataSource = [
            <Row>{
                reportId: <Column>{
                    type: ColumnType.TEXT,
                    text: response.id,
                    cellStyle: {
                        ellipsisTextLarge: true,
                    },
                },
                createdDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    text: response.createdDate,
                },
                status: <StatusColumn>{
                    type: ColumnType.STATUS,
                    text: findValueInDictionary(fraudReportStatuses, response.reportStatus, 'name_ru') ?? '-',
                    style: mapFraudReportStatusToStyle(response.reportStatus),
                },
                updatedDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    text: response.updatedDate,
                },
                signedAndSentDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    text: response.signedAndSentDate,
                },
                signedAndSentBy: <Column>{
                    type: ColumnType.TEXT,
                    text: response.signedAndSentBy ?? '-',
                },
                organization: <Column>{
                    type: ColumnType.TEXT,
                    text: response.organizationName ?? '-',
                },
                downloadExcelButton: this.createDownloadButton(response, FraudReportFileFormat.EXCEL),
                downloadPdfButton: this.createDownloadButton(response, FraudReportFileFormat.PDF),
            }
        ];
    }

    private createDownloadButton(response: FraudReportFilesResponse, reportType: FraudReportFileFormat): ButtonColumn {
        if (response.formats.includes(reportType)) {
            return <ButtonColumn>{
                type: ColumnType.BUTTON,
                buttonType: 'text',
                icon: 'ic_download',
                buttonSize: 'small',
                text: 'i18n_af_fraudReportFile#' + reportType,
                click: () => this.downloadReport(response.id, reportType),
            };
        }
        return null;
    }
}
